import { createContext, useContext } from 'react';

/*
 * useSupernatural.ts — 全画面「気配（超常）」演出の React 窓口（P2「端末は付喪神」基盤・2026-06-21）
 *
 * 後続 Wave が「このまま」使う公開 API。SupernaturalProvider 配下で useSupernatural() を呼ぶと、
 * 気配（継続 ambient + 一過性 pulse/connection）を制御するハンドルが得られる。
 *
 * 世界観ルール（設計書 §7.4）:
 *  - これは “現象” であって人格ではない。台詞を持たせない・全画面を別世界化しない。
 *  - 既定は控えめ（ambient 0.3〜0.5・最大 0.7 程度）。やりすぎ＝ホラー過多を避ける。
 */

/** 既存 line-dialog/effects と同じ歪み種別（質感を共有する）。 */
export type DistortionType = 'wave' | 'noise' | 'tear';

/**
 * 気配演出ハンドル。
 *
 * 合成モデル: 最終 intensity = max(ambient, transient)。
 *  - ambient: setAmbientDistortion で設定する “継続” の極薄の気配（mood=dread / 異界が近い）。
 *  - transient: pulse / playConnectionSequence による “一過性” の強い気配。終了すると ambient に戻る。
 *  - prefers-reduced-motion 時は Provider が最終 intensity をさらに弱める（視覚効果の最小化）。
 */
export interface SupernaturalApi {
  /**
   * 継続する極薄の気配を設定する（mood=dread / 異界が近い状態の “地” のノイズ）。
   * @param intensity 0..1。0 で継続気配を停止。範囲外は内部でクランプ。
   * @param type 歪みの質感（既定 'noise'）。
   */
  setAmbientDistortion(intensity: number, type?: DistortionType): void;

  /**
   * 一発の瞬間グリッチ（合図）。一定時間だけ intensity を持ち上げ、終わると ambient に戻る。
   * @param opts.intensity 既定 0.5。@param opts.durationMs 既定 700。@param opts.type 既定 'noise'。
   */
  pulse(opts?: { intensity?: number; durationMs?: number; type?: DistortionType }): void;

  /**
   * 起動「……接続」シーケンス。全画面 distortion + 文字化けする "……接続" を一定時間表示して収束する。
   * @param opts.intensity 既定 0.6。@param opts.durationMs 既定 1500。@param opts.label 既定 '……接続'。
   * @returns 実 duration[ms]（後続が phase 切替の待ち時間に使える）。
   */
  playConnectionSequence(opts?: { intensity?: number; durationMs?: number; label?: string }): number;

  /** オーバーレイ描画用: 現在いずれかの気配が出ているか（intensity>0 と同義）。 */
  active: boolean;
  /** オーバーレイ描画用: 合成後の現在 intensity（0..1・reduced-motion 補正後）。 */
  intensity: number;
}

/**
 * SupernaturalApi の React コンテキスト。
 * Provider 外で useSupernatural を呼んだ場合に分かりやすく失敗させるため既定は null。
 */
export const SupernaturalContextValue = createContext<SupernaturalApi | null>(null);

/**
 * 全画面気配演出への React 窓口。SupernaturalProvider 配下で呼ぶこと（外で呼ぶと throw）。
 */
export function useSupernatural(): SupernaturalApi {
  const ctx = useContext(SupernaturalContextValue);
  if (!ctx) {
    throw new Error('useSupernatural must be used within <SupernaturalProvider>');
  }
  return ctx;
}
